import React, { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import type { ItemCardDTO } from "@/types/pim";
import { clientEnv } from "../../../../env.client";
import { useSession } from "next-auth/react";
import { usePriceMap, useRequestPrices } from "./PricingContext";

// Tiny solid gray-100 PNG (matches the image container's bg) used as the blur
// placeholder. If VirtuosoGrid recycling remounts a card, the <Image> paints this
// immediately instead of flashing an empty box while the real image decodes.
const BLUR_DATA_URL =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAgAAAAICAIAAABLbSncAAAAEUlEQVR4nGP4/OUbVsQwtCQAmTG3Qel+Vt0AAAAASUVORK5CYII=";

const formatUSD = (amount: number) => {
  try {
    const n = typeof amount === "number" ? amount : Number(amount);
    const hasCents = Math.abs(n - Math.round(n)) > 1e-9;

    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: hasCents ? 2 : 0,
      maximumFractionDigits: 2,
    }).format(n);
  } catch (error) {
    return "$0";
  }
};

type ItemCardProps = {
  /**
   * The server-projected card DTO — the full PIMItem no longer crosses to the
   * client for grid surfaces (payload diet + structural pricing redaction).
   */
  item: ItemCardDTO | { isSkeleton: true; id: string };
  index: number;
  itemPageSlug?: string;
  onBeforeNavigate?: () => void;
};

const ItemCard: React.FC<ItemCardProps> = ({ item, itemPageSlug, onBeforeNavigate }) => {
  const [imageError, setImageError] = useState(false);
  const isSkeleton = 'isSkeleton' in item && item.isSkeleton;
  const hasImage = !isSkeleton && 'isSkeleton' in item === false && item.imageUrl && !imageError;
  // Derived, NOT state+effect: a card that mounts mid-scroll (session already
  // resolved) must render its final height on the FIRST paint — the old
  // effect-based flip made every newly mounted card grow a frame later, which
  // forced VirtuosoGrid to remeasure and visibly shift the grid while
  // scrolling logged-in. SSR and hydration both render false (session context
  // starts unresolved), so there is no mismatch; only the initially mounted
  // cards flip once when the session first resolves after load.
  const { data: session } = useSession();
  const isEmployee = !!session?.user?.name;

  // Pricing overlay (P3): SSR payloads are price-free for everyone; employees
  // fetch prices client-side. Registering the id is a no-op for anonymous
  // visitors and for ids already fetched.
  const requestPrices = useRequestPrices();
  const priceMap = usePriceMap();
  const itemId = 'isSkeleton' in item ? null : item.Id;
  useEffect(() => {
    if (isEmployee && itemId != null) requestPrices([itemId]);
  }, [isEmployee, itemId, requestPrices]);
  // undefined = not fetched yet (render a dash, not a misleading $0);
  // null = fetched, item genuinely has no price.
  const retail = itemId != null ? priceMap[itemId] : undefined;

  const handleClick = (e: React.MouseEvent) => {
    if (e.button !== 0) return; // Only handle left-click

    if (isSkeleton) {
      e.preventDefault();
      return;
    }
    onBeforeNavigate?.();
  };

  const capitalize = (str: string) => str.charAt(0).toUpperCase() + str.slice(1);

  return (
    <Link href={isSkeleton ? "#" : `/inventory/${itemPageSlug}`} onClick={handleClick} className="block">
      <div className={`bg-white border border-gray-200 rounded-lg shadow-sm flex flex-col group ${isSkeleton ? 'animate-pulse' : 'hover:cursor-pointer hover:shadow-xl active:shadow-xl transition-shadow duration-200'}`}>
        {/* Image */}
        <div className="aspect-square relative bg-gray-100 rounded-t-lg overflow-hidden">
          {isSkeleton ? (
            <div className="absolute inset-0 bg-gray-200"></div>
          ) : hasImage && 'isSkeleton' in item === false ? (
            <>
              <div className="relative size-full">
                <Image
                  src={`${clientEnv.CDN_BASE_URL}/inventory/${item.imageUrl}`}
                  alt={item.Name || 'Inventory item'}
                  fill
                  sizes="(max-width: 384px) 30vw, (max-width: 460px) 20vw, (max-width: 750px) 20vw, (max-width: 1024px) 30vw, (max-width: 1280px) 15vw, 20vw"
                  className="object-cover transition-transform duration-300 ease-in-out group-active:scale-105 group-hover:scale-105"
                  onError={() => setImageError(true)}
                  loading="eager"
                  placeholder="blur"
                  blurDataURL={BLUR_DATA_URL}
                />
                <span className="absolute 3xl:hidden bottom-1 left-1 items-center px-2 py-0.5 max-w-[95%] rounded-full w-fit text-nowrap overflow-hidden text-ellipsis text-xs font-medium bg-amber-100 text-black">
                  {item.categoryName || ''}
                </span>
              </div>
            </>
          ) : (
            <div className="absolute inset-0 flex items-center justify-center">
              <svg
                className="w-12 h-12 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1.5}
                  d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-3 flex flex-col">
          <div className="flex-1">
            {isSkeleton ? (
              <>
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-1.5"></div>
                <div className="h-4 bg-gray-200 rounded w-1/2 mb-2"></div>
                <div className="h-3 bg-gray-200 rounded w-full mb-1"></div>
                <div className="h-3 bg-gray-200 rounded w-2/3"></div>
              </>
            ) : 'isSkeleton' in item === false ? (
              <>
                <h2 className="text-md h-11 leading-5.5 line-clamp-2 font-semibold text-gray-900 mb-1.5">
                  {item.year ? `${item.year} ${item.Name}` : item.Name}
                </h2>
                <p className="text-sm text-gray-600 h-11 leading-5.5 line-clamp-2 mb-2">
                  {item.Description}
                </p>
              </>
            ) : null}
          </div>

          <div className="space-y-1.5 text-xs border-t border-gray-100 pt-2">
            {isSkeleton ? (
              <>
                <div className="h-6 bg-gray-200 rounded-full w-24"></div>
                <div className="flex justify-between items-center">
                  <div className="h-3 bg-gray-200 rounded w-12"></div>
                  <div className="h-3 bg-gray-200 rounded w-8"></div>
                </div>
                <div className="flex justify-between items-center">
                  <div className="h-3 bg-gray-200 rounded w-16"></div>
                  <div className="h-3 bg-gray-200 rounded w-12"></div>
                </div>
              </>
            ) : 'isSkeleton' in item === false ? (
              <>
                <div className="hidden 3xl:flex w-full justify-between items-center">
                  <span className="inline-flex items-center px-2 text-nowrap max-w-3/4 overflow-hidden text-ellipsis py-0.5 rounded-full text-sm font-medium bg-amber-100 text-black">
                    {item.categoryName || 'All'}
                  </span>
                  {item.condition ? (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-sm font-medium ${item.condition === "New" ? 'bg-green-100 text-green-800' : item.condition?.toLowerCase().trim() === "used" ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`}>
                      {capitalize(item.condition)}
                    </span>
                  ) : null}
                </div>

                <div className="flex 3xl:hidden w-full justify-between items-center">
                  {item.condition ? (
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-sm font-medium ${item.condition === "New" ? 'bg-green-100 text-green-800' : item.condition?.toLowerCase().trim() === "used" ? 'bg-yellow-100 text-yellow-800' : 'bg-gray-100 text-gray-800'}`}>
                      {capitalize(item.condition)}
                    </span>
                  ) : (
                    <span className="invisible inline-flex items-center px-2 py-0.5 rounded-full text-sm font-medium">.</span>
                  )}
                </div>

                <div className="flex flex-col justify-center items-start gap-1 text-sm">
                  <span className="text-gray-600">Location(s):</span>
                  <span className="block max-w-full text-gray-800 text-ellipsis overflow-hidden whitespace-nowrap">
                    {
                      item.locations.length >= 2 ?
                        <> {item.locations[0]}
                          <span> +{item.locations.length - 1} more</span>
                        </> : (
                          item.locations.length === 1 ? item.locations[0] : "N/A"
                        )
                    }
                  </span>
                </div>

                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-600">Quantity Available:</span>
                  <span className={`font-semibold min-w-1/2 text-right ${item.AvailableQty && item.AvailableQty > 10 ? 'text-green-600' : item.AvailableQty && item.AvailableQty > 0 ? 'text-yellow-600' : 'text-red-600'}`}>
                    {item.AvailableQty?.toLocaleString() || 0}
                  </span>
                </div>

                <div className={`flex flex-col 3xl:flex-row justify-start 3xl:justify-between items-start 3xl:items-center text-sm border-t border-gray-100 pt-2 ${isEmployee ? '' : 'hidden'}`}>
                  <div className="flex items-center gap-3 w-full 3xl:w-auto">
                    <span className="text-gray-600">Unit price:</span>
                    <span className="font-semibold text-green-600 flex-1 flex justify-end">
                      {retail === undefined ? '—' : formatUSD(retail ?? 0)}
                    </span>
                  </div>
                  <div className={`flex items-center gap-3 w-full 3xl:w-auto ${isEmployee && item.AvailableQty && item.AvailableQty > 1 ? '' : 'invisible'}`}>
                    <span className="text-gray-600">Total value:</span>
                    <span className="font-semibold text-green-600 flex-1 flex justify-end">
                      {retail === undefined ? '—' : formatUSD((retail ?? 0) * (item.AvailableQty || 0))}
                    </span>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>
    </Link>);
};

export default ItemCard;
